// Knowledge corpora — books/articles/transcripts the agents search before
// answering. (A subagent's profile is learned from them on the subagent:
// api/subagentBlueprints.ts.)

import { useMutation, useQueries, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

export type Corpus = components["schemas"]["Corpus"];
export type KnowledgeDocument = components["schemas"]["Document"];
export type IngestBody = components["schemas"]["IngestBody"];

const key = (customerId: string | null) => ["knowledge", customerId];
const base = (c: string) => `/api/customers/${c}/knowledge`;

/** `poll: false` for a reader that only needs names and counts (the Subagents cards). */
export function useCorpora(customerId: string | null, { poll = true }: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: key(customerId),
    enabled: customerId !== null,
    queryFn: ({ signal }) => apiFetch<Corpus[]>(base(customerId!), { signal }),
    refetchInterval: poll ? 20_000 : false,
  });
}

export type IndexJob = components["schemas"]["IndexJob"];

/** The corpus behind a project (created on first read), with its indexer's
 *  job record; polls while the indexer is running so counts move. */
export function useProjectCorpus(customerId: string, projectId: string | null) {
  return useQuery({
    queryKey: ["project-corpus", customerId, projectId],
    enabled: !!projectId,
    queryFn: ({ signal }) => apiFetch<Corpus>(`/api/customers/${customerId}/projects/${encodeURIComponent(projectId!)}/knowledge`, { signal }),
    refetchInterval: (q) => (q.state.data?.index_job?.live ? 2000 : false),
  });
}

/** Start the background indexer (returns its job; the corpus query polls it). */
export function useIndexProjectFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, projectId }: { customerId: string; projectId: string }) =>
      apiFetch<IndexJob>(`/api/customers/${customerId}/projects/${encodeURIComponent(projectId)}/knowledge/index`, { method: "POST" }),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["project-corpus", v.customerId] });
      qc.invalidateQueries({ queryKey: key(v.customerId) });
      qc.invalidateQueries({ queryKey: ["knowledge-docs", v.customerId] });
    },
  });
}

export function useCorpusDocuments(customerId: string, corpus: string | null) {
  return useQuery({
    queryKey: ["knowledge-docs", customerId, corpus],
    enabled: !!corpus,
    queryFn: ({ signal }) => apiFetch<KnowledgeDocument[]>(`${base(customerId)}/${encodeURIComponent(corpus!)}/documents`, { signal }),
  });
}

function invalidating<TArgs extends { customerId: string }, TOut>(fn: (args: TArgs) => Promise<TOut>) {
  return () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: fn,
      onSuccess: (_d, v) => {
        qc.invalidateQueries({ queryKey: key(v.customerId) });
        qc.invalidateQueries({ queryKey: ["knowledge-docs", v.customerId] });
      },
    });
  };
}

export const useCreateCorpus = invalidating(
  ({ customerId, name, description, agents }: { customerId: string; name: string; description: string; agents?: string[] }) =>
    apiFetch<Corpus>(base(customerId), { method: "POST", body: { name, description, agents } }),
);
export const useScopeCorpus = invalidating(
  ({ customerId, corpus, agents }: { customerId: string; corpus: string; agents: string[] }) =>
    apiFetch<Corpus>(`${base(customerId)}/${encodeURIComponent(corpus)}/scope`, { method: "PUT", body: { agents } }),
);
export const useDeleteCorpus = invalidating(({ customerId, corpus }: { customerId: string; corpus: string }) =>
  apiFetch<{ removed: boolean }>(`${base(customerId)}/${encodeURIComponent(corpus)}`, { method: "DELETE" }),
);
export const useIngest = invalidating(({ customerId, corpus, body }: { customerId: string; corpus: string; body: IngestBody }) =>
  apiFetch<{ id: string; chunks: number }>(`${base(customerId)}/${encodeURIComponent(corpus)}/ingest`, { method: "POST", body: body }),
);
export const useDeleteDocument = invalidating(({ customerId, corpus, docId }: { customerId: string; corpus: string; docId: string }) =>
  apiFetch<{ removed: boolean }>(`${base(customerId)}/${encodeURIComponent(corpus)}/documents/${encodeURIComponent(docId)}`, { method: "DELETE" }),
);

export interface ProjectLibraryState {
  corpus: string | null;
  documents: number;
  /** Its files are being read now. */
  reading: boolean;
  /** The last read stopped; the line says so. */
  failed: boolean;
}

export const projectLibraryKey = (customerId: string, projectId: string) => ["project-corpus", customerId, projectId];

/** Each project's library as it stands: the corpus list for the names and counts, the job for whether it is being read. */
export function useProjectLibraries(customerId: string | null, projectIds: readonly string[]) {
  return useQueries({
    queries: projectIds.map((projectId) => ({
      queryKey: projectLibraryKey(customerId ?? "", projectId),
      enabled: !!customerId,
      queryFn: ({ signal }: { signal: AbortSignal }) => apiFetch<Corpus>(`/api/customers/${customerId}/projects/${encodeURIComponent(projectId)}/knowledge`, { signal }),
      refetchInterval: (q: { state: { data?: Corpus } }) => (q.state.data?.index_job?.live ? 2000 : false),
    })),
  });
}

export function libraryStateOf(c: Corpus | undefined): ProjectLibraryState {
  const job = c?.index_job;
  return { corpus: c?.name ?? null, documents: c?.documents ?? 0, reading: !!job?.live, failed: !job?.live && !!job?.error && !c?.documents };
}

/**
 * A project picked where it has no library yet: made, and its files read. The
 * same two calls Details → Library makes. Returns the library's name.
 */
export async function ensureProjectLibrary(qc: QueryClient, customerId: string, projectId: string): Promise<string> {
  const corpus = await apiFetch<Corpus>(`/api/customers/${customerId}/projects/${encodeURIComponent(projectId)}/knowledge`);
  if (!corpus.documents && !corpus.index_job?.live) {
    await apiFetch<IndexJob>(`/api/customers/${customerId}/projects/${encodeURIComponent(projectId)}/knowledge/index`, { method: "POST" });
  }
  void qc.invalidateQueries({ queryKey: ["project-corpus", customerId] });
  void qc.invalidateQueries({ queryKey: key(customerId) });
  return corpus.name;
}
